<template>
  <div class="schedule-report">
    <DeleteModal
      class="delete-modal"
      :show="showDeleteModal"
      :reportId="value.reportId"
      @delete="deleteSchedule"
      @cancel="closeDeleteModal"
    >
      <template v-slot:header>Delete Schedule</template>
      <template v-slot:content>
        Are you sure you want to delete schedule for
        {{ data.reportName }} <br />
        This action cannot be undone
      </template>
      <template v-slot:cancel>Cancel</template>
      <template v-slot:complete>Yes, delete schedule</template>
    </DeleteModal>
    <SlideOut
      :show="show"
      class="schedule-report"
      :completeDisabled="!(isDataValid && !isDataClean)"
      @complete="handleComplete"
      @cancel="$emit('cancel')"
    >
      <template v-slot:header>{{ slideOutTitle }}</template>
      <template v-slot:content>
        <p v-if="!isEditing" class="content-text">
          By scheduling an already saved report,
          you will be saving a <b>new report</b> to your Saved Reports section.
          Please fill in all information below and click Save and Schedule.
        </p>
        <MessageBarV02
          v-if="data.error"
          icon="mdi-alert-circle"
          class="error-message-bar"
          type="error"
          dark
          dense
          dismissible
          tile
          transition="fade-transition"
        >
          {{ data.error }}
        </MessageBarV02>
        <TextInput
          :required="!data.reportName"
          v-model="data.reportName"
          name="report-name"
          label="Report Name"
          class="search-input"
        ></TextInput>
        <Email
          class="section"
          v-model="data"
          :current-user-email="currentUserEmail"
          :set-error="setError"
        >
        </Email>
        <Schedule
          class="section"
          v-model="data"
          :isNewReport="!isEditing"
          :reportDataWindowStart="reportDataWindowStart"
          :reportDataWindowEnd="reportDataWindowEnd"
        ></Schedule>
      </template>
      <template v-slot:customActions>
        <Anchor v-if="isEditing && isScheduled" @click="openDeleteModal">
          <Icon
            :color="deleteButtonColor"
            class="icon"
          > mdi-delete
          </Icon>
          Delete Schedule
        </Anchor>
        <SecondaryButton @click="sendPreview" :disabled="shouldDisableSendPreview">
          <slot name="send-preview">Send Preview</slot>
        </SecondaryButton>
      </template>
      <template v-slot:complete>Save and Schedule</template>
    </SlideOut>
  </div>
</template>

<script>
import {
  SlideOut,
  Anchor,
  Icon,
  TextInput,
  SecondaryButton,
  MessageBarV02,
} from '@mikmak/components';
import { mapState } from 'vuex';
import { addDays, addMonths } from 'date-fns';
import {
  getReport,
  getFirstDayOfDelivery,
} from
  '../../../../services/dashboard/scheduledReportService';
import Email from '../ScheduleReport/Email';
import Schedule from '../ScheduleReport/Schedule';
import DeleteModal from '../DeleteModal';

export default {
  components: {
    SlideOut,
    Anchor,
    Icon,
    TextInput,
    Email,
    Schedule,
    SecondaryButton,
    MessageBarV02,
    DeleteModal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    isEditing: {
      type: Boolean,
    },
    customReportName: {
      type: String,
      default: '',
    },
    dataWindowStart: {
      type: String,
    },
    dataWindowEnd: {
      type: String,
    },
  },
  async mounted() {
    await this.getReportData();
    this.resetApiData();
  },
  data() {
    return {
      showDeleteModal: false,
      deleteButtonColor: '#CB4444',
      isFormDirty: false,
      apiData: {},
      forceDisableOnSendPreview: false,
      data: {
        reportName: '',
        emailRecipients: [this.currentUserEmail],
        emailSubject: '',
        bodyMessage: '',
        startDate: addDays(new Date(), 1),
        endDate: addMonths(addDays(new Date(), 1), 3),
        emailFrequency: 'daily',
        hasEndDate: false,
        fileType: 'csv',
        aggregateDays: 1,
        error: '',
        filterJson: { dateRange: { startDate: '', endDate: '' } },
      },
    };
  },
  methods: {
    async fetchApiData(reportId) {
      try {
        const token = await this.$auth.getTokenSilently();
        const report = await getReport(reportId, token);

        if (report) {
          this.data = this.adjustDataForComponent(report.data);

          this.resetApiData();
        }

      } catch (error) {
        throw new Error(error);
      }
    },
    deleteSchedule() {
      this.data.emailFrequency = '';
      this.data.startDate = undefined;
      this.data.endDate = undefined;

      this.$emit('deleteReportSchedule', {
        ...this.data,
      });

      this.data.startDate = addDays(new Date(), 1);
      this.data.endDate = addMonths(addDays(new Date(), 1), 3);

      this.apiData = { ...this.data };

      this.showDeleteModal = false;
    },
    async getReportData() {
      if (this.value.scheduleReportId) {
        await this.fetchApiData(this.value.scheduleReportId);
      } else if (this.customReportName) {
        this.data.reportName = this.customReportName;
      }
    },
    sendPreview() {
      this.$emit('sendPreview', {
        ...this.data,
        emailRecipients: [this.currentUserEmail],
      });

      this.forceDisableOnSendPreview = true;
      setTimeout(() => this.forceDisableOnSendPreview = false, 5000);
    },
    handleComplete() {
      this.$emit('saveAndScheduleReport', {
        ...this.data,
      });

      this.apiData = { ...this.data };
    },
    resetData() {
      this.data = {
        reportName: '',
        emailRecipients: [this.currentUserEmail],
        emailSubject: '',
        bodyMessage: '',
        startDate: addDays(new Date(), 1),
        endDate: addMonths(addDays(new Date(), 1), 3),
        emailFrequency: 'daily',
        hasEndDate: false,
        fileType: 'csv',
        aggregateDays: 1,
        error: '',
        filterJson: { dateRange: { startDate: '', endDate: '' } },
      };

      this.resetApiData();
    },
    resetApiData() {
      this.apiData = {
        ...this.data,
        emailRecipients: [ ...this.data.emailRecipients ],
      };
    },
    getScheduleValueFromString(value) {
      return value.split(', ')[0].toLowerCase();
    },
    adjustDataForComponent(data) {
      const today = new Date();
      const tomorrow = addDays(today, 1);
      const threeMonthsFromToday = addMonths(tomorrow, 3);

      return {
        ...data,
        startDate: new Date(data.startDate || tomorrow),
        endDate: new Date(data.endDate || threeMonthsFromToday),
        reportName: data.reportName || data.name,
        emailRecipients: data.recipientEmails,
        emailFrequency: data.schedule ? this.getScheduleValueFromString(data.schedule) : 'not scheduled',
        emailSubject: data.emailSubject || '',
        hasEndDate: !!data.endDate,
        aggregateDays: data.aggregateDays || 1,
        error: '',
      };
    },
    setError(message) {
      this.data.error = message;

      this.$forceUpdate();
    },
    openDeleteModal() {
      this.showDeleteModal = true;

      this.$emit('changeScheduleReportId', this.data.reportId);
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    cancelSlideOut() {
      this.data = { ...this.apiData };

      this.$emit('cancel');
    },
  },
  computed: {
    ...mapState({
      currentUserEmail: state => state.main.currentUser.email,
    }),
    shouldDisableSendPreview() {
      const hasReportName = !!this.data.reportName;
      const hasSubject = !!this.data.emailSubject;
      const hasFileType = !!this.data.fileType;
      const hasError = !!this.data.error;
      const hasAggregateDays = !!this.data.aggregateDays;

      return this.forceDisableOnSendPreview
        || hasError
        || !hasSubject
        || !hasReportName
        || !hasFileType
        || !hasAggregateDays;
    },
    isDataValid() {
      const hasReportName = !!this.data.reportName;
      const hasSubject = !!this.data.emailSubject;
      const hasStartDate = !!this.data.startDate;
      const hasEndDateValue = !!this.data.endDate;
      const hasEmailFrequency = !!this.data.emailFrequency;
      const hasFileType = !!this.data.fileType;
      const hasAggregateDays = !!this.data.aggregateDays;
      const hasEmailRecipients = !!this.data.emailRecipients.length;
      const hasError = !!this.data.error;
      const firstDateOfDelivery
        = getFirstDayOfDelivery(this.data.emailFrequency, this.data.startDate);

      if (this.data.hasEndDate && !hasEndDateValue) {
        return false;
      }

      if (this.data.endDate < this.data.startDate) {
        return false;
      }

      if (this.data.endDate < firstDateOfDelivery) {
        return false;
      }

      return hasReportName
        && hasSubject
        && hasStartDate
        && hasEmailFrequency
        && hasFileType
        && hasAggregateDays
        && hasEmailRecipients
        && !hasError;
    },
    isDataClean() {
      return this.apiData.reportName === this.data.reportName
        && this.apiData.emailSubject === this.data.emailSubject
        && this.apiData.startDate === this.data.startDate
        && this.apiData.endDate === this.data.endDate
        && this.apiData.fileType === this.data.fileType
        && this.apiData.aggregateDays === this.data.aggregateDays
        && this.apiData.emailFrequency === this.data.emailFrequency
        && this.apiData.emailRecipients.length === this.data.emailRecipients.length;
    },
    slideOutTitle() {
      return this.isEditing
        ? 'Edit Scheduled Report'
        : 'Schedule a New Report';
    },
    isScheduled() {
      const { emailFrequency = '' } = this.apiData;

      return emailFrequency && emailFrequency !== 'not scheduled';
    },
    reportDataWindowStart() {
      if (this.isEditing) {
        return this.data.filterJson.dateRange.startDate;
      } else {
        return this.dataWindowStart;
      }
    },
    reportDataWindowEnd() {
      if (this.isEditing) {
        return this.data.filterJson.dateRange.endDate;
      } else {
        return this.dataWindowEnd;
      }
    },
  },
  watch: {
    async show(newVal) {
      if (newVal) {
        this.$store.commit('updateActiveView', 'Schedule Report');

        await this.getReportData();
      } else {
        this.$emit('changeScheduleReportId', '');
        this.resetData();
      }
    },
  },
};
</script>
<style scoped lang="scss">
.delete-modal {
  position: absolute;
  z-index: 3;
}

.schedule-report.slide-out ::v-deep {
  .subheader {
    font-size: 16px;
    line-height: 19px;
    color: #778699;
    margin-bottom: 24px;
  }

  .section {
    margin: 20px 0;
    display: flex;
  }

  .section:last-of-type {
    margin-bottom: 225px;
  }

  .error-message-bar {
    position: absolute;
    top: 0;
    width: 100%;
    right: 0;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .actions .link {
    position: absolute;
    left: 20px;
    bottom: 30px;
    width: 100%;
  }

  .actions .link > * {
    display: flex;
    align-items: center;
    color: #CB4444;
  }

  .icon {
    margin-right: 10px;
  }

  .content-text {
    margin: 0px 0px 12px 0px;
  }
}

</style>
